import React, { type KeyboardEvent, memo, type ReactNode } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import ArrowCurvedDownRightIcon from '@atlaskit/icon-lab/core/arrow-curved-down-right';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type ActionableFollowUpLinkProps = {
	children: ReactNode;
	onClick: () => void;
	isDisabled?: boolean;
	testId?: string;
	ariaLabel?: string;
	/**
	 * - `block` (default): a standalone link with a leading arrow icon, intended for a
	 *   vertical stack of follow-ups.
	 * - `inline`: a link that flows within surrounding prose, keeping its leading arrow
	 *   icon but wrapping at word boundaries like normal text (never forced onto its own
	 *   line).
	 */
	appearance?: 'block' | 'inline';
};

const styles = cssMap({
	// Base styles shared by both appearances. NOTE: `display` is intentionally NOT set
	// here — each appearance owns its own `display` so the two never conflict in the
	// atomic stylesheet (where insertion order, not cx() order, decides the winner).
	root: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.0'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		textAlign: 'left',
		'&:hover': {
			color: token('color.text'),
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	blockRoot: {
		// Standalone link for a vertical stack. Use `inline-flex` with the icon and
		// label as flex siblings, and align them to the top so the icon stays fixed
		// on the first line. `alignItems: flex-start` (instead of `center`) keeps the
		// icon anchored to the first line rather than vertically centering against a
		// multi-line label.
		display: 'inline-flex',
		alignItems: 'flex-start',
		gap: token('space.100'),
		maxWidth: '100%',
	},
	inlineRoot: {
		// Flow as part of the surrounding text so the link text breaks across lines
		// mid-sentence (even mid-word if it overflows) like normal prose. This is a
		// plain inline `<span role="button">` rather than a native <button>, because
		// browsers treat a <button> as one atomic inline box that cannot split its
		// text across line boxes.
		display: 'inline',
		whiteSpace: 'normal',
		verticalAlign: 'baseline',
		cursor: 'pointer',
		// Allow the label (and arrow) to break across lines like text.
		overflowWrap: 'anywhere',
		'&:hover': {
			color: token('color.text'),
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	inlineDisabled: {
		cursor: 'not-allowed',
		opacity: token('opacity.disabled'),
	},
	inlineIcon: {
		// Keep the arrow inline and glued to the start of the link text.
		display: 'inline',
		verticalAlign: 'middle',
		marginInlineEnd: token('space.050'),
	},
	disabled: {
		opacity: token('opacity.disabled'),
	},
	icon: {
		flexShrink: 0,
	},
	blockIcon: {
		display: 'inline-flex',
	},
	label: {
		minWidth: '0px',
		textDecoration: 'underline',
		textUnderlineOffset: token('space.025'),
	},
	blockLabel: {
		overflowWrap: 'anywhere',
	},
	inlineLabel: {
		// `display: inline` so the underlined text breaks across lines with the prose,
		// including mid-word when a single label would overflow the line.
		display: 'inline',
		overflowWrap: 'anywhere',
	},
});

const ActionableFollowUpLinkComponent = ({
	children,
	onClick,
	isDisabled = false,
	testId,
	ariaLabel,
	appearance = 'block',
}: ActionableFollowUpLinkProps): React.JSX.Element => {
	const isInline = appearance === 'inline';

	const content = (
		<React.Fragment>
			<Box as="span" xcss={cx(styles.icon, isInline ? styles.inlineIcon : styles.blockIcon)}>
				<ArrowCurvedDownRightIcon label="" size="small" />
			</Box>
			<Box as="span" xcss={cx(styles.label, isInline ? styles.inlineLabel : styles.blockLabel)}>
				{children}
			</Box>
		</React.Fragment>
	);

	if (isInline) {
		// A native <button> is an atomic inline box whose text cannot break across
		// line boxes, so a long inline link would wrap as a whole unit onto its own
		// line. To let the link text flow and break mid-line with the surrounding
		// prose, render an inline `<span role="button">` with keyboard support.
		const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
			if (isDisabled) {
				return;
			}
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onClick();
			}
		};

		return (
			<Box
				as="span"
				role="button"
				tabIndex={isDisabled ? -1 : 0}
				aria-label={ariaLabel}
				aria-disabled={isDisabled || undefined}
				onClick={isDisabled ? undefined : onClick}
				onKeyDown={handleKeyDown}
				testId={testId}
				xcss={cx(styles.root, styles.inlineRoot, isDisabled && styles.inlineDisabled)}
			>
				{content}
			</Box>
		);
	}

	return (
		<Pressable
			aria-label={ariaLabel}
			isDisabled={isDisabled}
			onClick={onClick}
			testId={testId}
			xcss={cx(styles.root, styles.blockRoot, isDisabled && styles.disabled)}
		>
			{content}
		</Pressable>
	);
};

export const ActionableFollowUpLink: React.MemoExoticComponent<
	(props: ActionableFollowUpLinkProps) => React.JSX.Element
> = memo(ActionableFollowUpLinkComponent);
